import React, { useEffect, useState } from 'react';

import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { tasksInformationActions } from '@entities/task/model/slice/tasksInformation';
import { TaskStatusSwitcher } from '@features/TaskStatusSwitcherBlock/ui/TaskStatusSwitcher';
import { Icon, Tooltip } from '@shared/ui';

import cls from './IconsWrapper.module.css';
import { useAppDispatch, useAppSelector } from '../../../../store';
import { changeCustomTaskStatus } from '../../api';
import { Task, TTaskStatus } from '../../model/types/tasks';
import { ListTaskDatePicker } from '../DatePicker';

interface IconsWrapperProps {
    taskId: string;
    entityUid: string;
    currentStatus: Task['context']['status'];
    isHovered: boolean;
    classes?: string;
}

export const IconsWrapper = ({
    taskId,
    entityUid,
    isHovered,
    currentStatus,
    classes,
}: IconsWrapperProps) => {
    const { t } = useTranslation();
    const [isVisible, setIsVisible] = useState({
        calendarIcon: false,
        deleteIcon: false,
        taskStatusIcon: false,
    });
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);

    const dispatch = useAppDispatch();
    const taskData: Task = useAppSelector((state) => state.tasksReducer.entities[taskId]);
    const [taskSwitcherStatus, setTaskSwitcherStatus] = useState<TTaskStatus>(
        taskData.context.status
    );

    useEffect(() => {
        setTaskSwitcherStatus(taskData?.context?.status || 'is_waiting');
    }, [taskData]);

    const onHoverHandle = (iconType: 'calendarIcon' | 'deleteIcon' | 'taskStatusIcon') => {
        setIsVisible((prev) => ({ ...prev, [iconType]: true }));
    };

    const onLeaveHandle = (iconType: 'calendarIcon' | 'deleteIcon' | 'taskStatusIcon') => {
        setIsVisible((prev) => ({ ...prev, [iconType]: false }));
    };

    const onSwitchStatusClick = async (e: React.SyntheticEvent) => {
        e.stopPropagation();
        setTaskSwitcherStatus((prev) => (prev === 'is_waiting' ? 'in_progress' : 'is_waiting'));

        const res = await dispatch(
            changeCustomTaskStatus({
                taskId,
                entityUid,
                switcherStatus: taskSwitcherStatus === 'is_waiting' ? 'in_progress' : 'is_waiting',
            })
        ).unwrap();
        dispatch(tasksInformationActions.updateTabs(res.newTabs));
    };

    return (
        <div className={classNames(cls.IconsWrapper, [classes])}>
            {(isHovered || isCalendarOpen) &&
                taskData.context.highest_role === 'author' &&
                !taskData.completed && (
                    <ListTaskDatePicker
                        taskId={taskId}
                        onShow={() => setIsCalendarOpen(true)}
                        onHide={() => setIsCalendarOpen(false)}
                    >
                        <span
                            className={classNames(cls.Icon__Cursor__pointer, {
                                [cls.visible]: isHovered || isCalendarOpen,
                            })}
                            onMouseEnter={() => onHoverHandle('calendarIcon')}
                            onMouseLeave={() => onLeaveHandle('calendarIcon')}
                            onClick={(e) => {
                                // console.log('clicked calendar!');
                                e.stopPropagation();
                            }}
                        >
                            <Tooltip
                                textContent={t('Task.IconsWrapper.CalendarIcon.Tooltip.text')}
                                classes={isVisible.calendarIcon ? cls.tooltipPosition : cls.hidden}
                            />
                            <span className={cls.Icon_span}>
                                <Icon iconName="taskCalendar" />
                            </span>
                        </span>
                    </ListTaskDatePicker>
                )}
            {currentStatus === 'in_progress' &&
                taskData.context.highest_role != 'observer' &&
                !taskData.completed && (
                    <span
                        className={cls.Icon__Cursor__pointer}
                        onMouseEnter={() => onHoverHandle('taskStatusIcon')}
                        onMouseLeave={() => onLeaveHandle('taskStatusIcon')}
                        onClick={onSwitchStatusClick}
                    >
                        <Tooltip
                            textContent={
                                taskSwitcherStatus === 'is_waiting'
                                    ? t('Task.IconsWrapper.taskSwitcherStatus.Tooltip.is_waiting')
                                    : t('Task.IconsWrapper.taskSwitcherStatus.Tooltip.in_progress')
                            }
                            classes={isVisible.taskStatusIcon ? cls.tooltipPosition : cls.hidden}
                        />
                        <TaskStatusSwitcher
                            status={taskSwitcherStatus}
                            isLoading={taskData.isStatusLoading}                            
                        />
                    </span>
                )}
            {currentStatus === 'in_progress' &&
                taskData.context.highest_role === 'observer' &&
                !taskData.completed && (
                    <span
                        className={cls.Icon}
                        onMouseEnter={() => onHoverHandle('taskStatusIcon')}
                        onMouseLeave={() => onLeaveHandle('taskStatusIcon')}
                    >
                        <Tooltip
                            textContent={
                                taskSwitcherStatus === 'is_waiting'
                                    ? t('Task.IconsWrapper.taskSwitcherStatus.Tooltip.is_waiting')
                                    : t('Task.IconsWrapper.taskSwitcherStatus.Tooltip.in_progress')
                            }
                            classes={isVisible.taskStatusIcon ? cls.tooltipPosition : cls.hidden}
                        />
                        <TaskStatusSwitcher
                            status={taskSwitcherStatus}
                            isLoading={taskData.isStatusLoading}
                            classes={cls.CursorDefault}
                        />
                    </span>
                )}
            {isHovered &&
                currentStatus === 'is_waiting' &&
                taskData.context.highest_role != 'observer' &&
                !taskData.completed && (
                    <span
                        className={cls.Icon__Cursor__pointer}
                        onMouseEnter={() => onHoverHandle('taskStatusIcon')}
                        onMouseLeave={() => onLeaveHandle('taskStatusIcon')}
                        onClick={onSwitchStatusClick}
                    >
                        <Tooltip
                            textContent={
                                taskSwitcherStatus === 'is_waiting'
                                    ? t('Task.IconsWrapper.taskSwitcherStatus.Tooltip.is_waiting')
                                    : t('Task.IconsWrapper.taskSwitcherStatus.Tooltip.in_progress')
                            }
                            classes={isVisible.taskStatusIcon ? cls.tooltipPosition : cls.hidden}
                        />
                        <TaskStatusSwitcher
                            status={taskSwitcherStatus}
                            isLoading={taskData.isStatusLoading}
                        />
                    </span>
                )}
        </div>
    );
};
